import axios, {
  AxiosError,
  isAxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig
} from 'axios';
import vueI18n from '@/plugins/vueI18n';
import { ROUTE_NAMES } from '@/router/conf';
import router from '@/router';
import type { IDetailsResponse } from '@/types/services/user.service';
import { queryClient } from './vueQueryPlugin';
import {
  AUTH_BROADCAST_CHANNEL,
  EAuthMessages,
  type IAuthUserRefreshedMessage
} from '@/types/composables/useAuthChannel.const';
import dayjs from 'dayjs';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_PAGE_EXPIRED = 419;
const REFRESH_TOKEN_TIMEOUT = 30000; // 30 seconds

// Map to track ongoing refresh promises per user
const refreshPromises = new Map<string, Promise<void>>();

const extractUserIndex = (url?: string): string | false => {
  const userIndex = url?.match(/\/u\/(\d+)\//)?.[1];
  if (!userIndex) {
    console.warn('Could not extract user index from URL: ', url);
    return false;
  }
  return userIndex;
};

const TOKEN_LAST_REFRESH_KEY = (userIndex: string) => `token_last_refresh_u_${userIndex}`;

const broadcastTokenRefreshed = (userIndex: string): void => {
  try {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.postMessage({
      message: EAuthMessages.USER_REFRESHED,
      uids: [Number(userIndex)]
    } satisfies IAuthUserRefreshedMessage);
    channel.close();
  } catch {
    // BroadcastChannel may not be available in all contexts
  }
};

const refreshAndRetry = async (error: AxiosError) => {
  if (!error.config) {
    return Promise.reject(error);
  }

  const userIndex = extractUserIndex(error.config.url);
  if (userIndex === false) {
    return Promise.reject(error);
  }

  // Check if a refresh is already in progress for this user
  const existingRefreshPromise = refreshPromises.get(userIndex);
  if (existingRefreshPromise) {
    // Wait for the ongoing refresh to complete
    console.info('Waiting for ongoing token refresh for user', userIndex);
    try {
      await existingRefreshPromise;
      // Refresh succeeded, retry the original request
      console.info('Retrying queued request after refresh:', error.config.url);
      return axiosInstance.request(error.config);
    } catch {
      // Refresh failed, reject with original 401 error
      console.warn('Token refresh failed, rejecting queued request with original error');
      return Promise.reject(error);
    }
  }

  // Create a new refresh promise for this user
  const refreshPromise = (async () => {
    console.info('Starting token refresh for user', userIndex);

    const performRefresh = async () => {
      // Wrap refresh request with timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Token refresh timeout after ${REFRESH_TOKEN_TIMEOUT}ms`));
        }, REFRESH_TOKEN_TIMEOUT);
      });

      const refreshResponse = await Promise.race([
        axiosInstance.post(`/u/${userIndex}/auth/refresh`),
        timeoutPromise
      ]);

      console.debug('Refresh response:', refreshResponse);

      // If refresh request receives a non 2xx response, redirect to log in
      if (refreshResponse.status < 200 || refreshResponse.status >= 300) {
        throw new Error('Refresh failed with non-2xx status');
      }

      console.info('Token refresh successful for user', userIndex);
      localStorage.setItem(TOKEN_LAST_REFRESH_KEY(userIndex), dayjs().valueOf().toString());
      broadcastTokenRefreshed(userIndex);
    };

    // Record when this tab started waiting so we can detect if another tab
    // refreshed while we were queued behind the Web Lock
    const waitingSince = dayjs();

    try {
      if (navigator.locks) {
        await navigator.locks.request(`token-refresh-user-${userIndex}`, async () => {
          // After acquiring the lock, check if another tab refreshed while we waited
          const lastRefreshValue = localStorage.getItem(TOKEN_LAST_REFRESH_KEY(userIndex));
          const lastRefresh = lastRefreshValue ? dayjs(Number(lastRefreshValue)) : null;
          if (lastRefresh !== null && lastRefresh.isAfter(waitingSince)) {
            console.info('Token already refreshed by another tab for user', userIndex);
            return;
          }
          await performRefresh();
        });
      } else {
        // Fallback: no cross-tab coordination
        await performRefresh();
      }
    } catch (refreshError) {
      console.warn(
        `/u/${userIndex}/auth/refresh request failed, redirecting to login`,
        refreshError
      );

      // Clear the promise before redirect to prevent queued requests from retrying
      refreshPromises.delete(userIndex);

      await axiosInstance.post(`/u/${userIndex}/auth/logout`);
      await router.push({
        name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
        query: {
          uid: userIndex,
          reason: 'session-expired'
        }
      });

      throw refreshError;
    } finally {
      // Always clean up the promise from the map
      refreshPromises.delete(userIndex);
    }
  })();

  // Store the promise in the map
  refreshPromises.set(userIndex, refreshPromise);

  try {
    await refreshPromise;
    // Refresh succeeded, retry the original request
    console.info('Retrying request:', error.config.url);
    return axiosInstance.request(error.config);
  } catch {
    // Refresh failed, reject with original 401 error
    return Promise.reject(error);
  }
};

const waitForRefresh = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  // Exempt critical paths to prevent XSRF/auth deadlock
  if (
    config.url === '/' ||
    config.url?.endsWith('/auth/refresh') ||
    config.url?.endsWith('/auth/logout') ||
    config.url?.endsWith('/auth/logoutAll')
  ) {
    return config;
  }

  const userIndex = extractUserIndex(config.url);
  if (userIndex === false) {
    return config;
  }

  // Check if a refresh is in progress for this user
  const refreshPromise = refreshPromises.get(userIndex);
  if (refreshPromise) {
    console.info('Request waiting for token refresh to complete:', config.url);
    try {
      await refreshPromise;
      console.info('Token refresh completed, proceeding with request:', config.url);
    } catch {
      // Refresh failed, but let the request proceed anyway
      // It will get a 401 and go through the normal refresh flow
      console.debug('Token refresh failed, but proceeding with request:', config.url);
    }
  }

  return config;
};

const handleAfterLogout = (url: string | undefined) => {
  if (!url?.includes('/auth/logout')) {
    return;
  }
  const userIndex = extractUserIndex(url);
  if (userIndex === false) {
    return;
  }
  // After logout, remove all authenticated queries for the user
  queryClient.removeQueries({
    queryKey: ['authenticatedQuery', userIndex],
    exact: false
  });
  queryClient.invalidateQueries({
    queryKey: ['getAccounts']
  });
};

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  // Let's see how it goes. I want to have some sensible default by the end of
  // this project
  timeout: Number(import.meta.env.VITE_API_TIMEOUT ?? 15000),
  withCredentials: true,
  withXSRFToken: true
});

axiosInstance.interceptors.request.use(
  async (config) => {
    // Do something before request is sent
    config.headers['Accept-Language'] =
      vueI18n.global.locale.value !== '*' ? vueI18n.global.locale.value : 'en-US,en;q=0.5';
    const userIndex = extractUserIndex(config.url);
    if (userIndex !== false) {
      // Hardcoding the key here since otherwise we would have to handle the context issue with useQueryClient inside useUserService
      const userDetails = queryClient.getQueryData<IDetailsResponse>([
        'authenticatedQuery',
        userIndex,
        'user',
        'details'
      ]);
      if (userDetails) {
        config.headers['X-User-id'] = userDetails.id;
        config.headers['X-User-WD-id'] = userDetails.wdId;
      }
    }
    return waitForRefresh(config);
  },
  (error) => {
    // Do something with request error
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    handleAfterLogout(response.config.url);
    return response;
  },
  async (error) => {
    if (isAxiosError(error) && error.response && error.config) {
      handleAfterLogout(error.config.url);
      // The request was made and the server responded with a status code
      // Handle 401 Unauthorized exceptions when the request was not a refresh token request
      // already and the user is not on the login page
      if (
        error.response.status === HTTP_UNAUTHORIZED &&
        !['/auth/refresh', '/auth/logout', '/auth/logoutAll'].some((path) =>
          error.config?.url?.endsWith(path)
        ) &&
        !router.currentRoute.value.matched.some((route) => route.name === ROUTE_NAMES.AUTH_LAYOUT)
      ) {
        console.info('Interceptor caught unauthorized request', error.config.url);

        return await refreshAndRetry(error);
      } else if (error.response.status === HTTP_PAGE_EXPIRED && navigator.cookieEnabled) {
        // XSRF token has expired
        console.info('XSRF token has expired. Refreshing!');
        // Update the token and then retry the original request
        await axiosInstance.get('/');
        console.info('XSRF token refreshed. Trying original request again!');
        return await axiosInstance.request(error.config);
      } else if ([HTTP_FORBIDDEN].includes(error.response.status)) {
        console.info('Interceptor caught forbidden request', error.config.url);
        await axiosInstance.post(`/u/${router.currentRoute.value.params.userIndex}/auth/logout`);
        await router.push({
          name: ROUTE_NAMES.AUTH_LOGIN_USER_SELECT,
          query: {
            uid: router.currentRoute.value.params.userIndex,
            reason: 'invalid-session'
          }
        });
        return Promise.reject(error);
      }
    }

    // Do something with response error
    return Promise.reject(error);
  }
);

export default axiosInstance;
